<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import brokenPeanut from '@/assets/broken-peanut.png';
import Caption from '@/components/base/typography/Caption.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import ADLineItemDetails from '@/components/checkout/ADLineItemDetails.vue';
import GiftDetailsModal from '@/components/digital-gift/GiftDetailsModal.vue';
import money from '@/filters/money';
import { NutsLineItem } from '@/lib/cart/lineItem';

const props = withDefaults(
  defineProps<{
    lineItem: NutsLineItem;
    showPricing?: boolean;
  }>(),
  {
    showPricing: true,
  },
);

const route = useRoute();
const isReceiptPage = computed(() => route?.name === 'CheckoutReceipt');

const isDigitalGift = computed(() => !!props.lineItem.custom?.fields.recipientsJson);

const digitalGiftingFields = computed(() => ({
  occasionImageUrl: props.lineItem.custom?.fields.occasionImageUrl,
  senderName: props.lineItem.custom?.fields.senderName,
  sendAt: props.lineItem.custom?.fields.sendAt,
  recipients: props.lineItem.custom?.fields.recipientsJson
    ? JSON.parse(props.lineItem.custom?.fields.recipientsJson)
    : '',
}));

const showGiftDetails = ref(false);

const handleOpenDetails = () => {
  showGiftDetails.value = true;
};
const handleCloseDetails = () => {
  showGiftDetails.value = false;
};

const autoDeliveryIntervalWeeks = computed(() =>
  props.lineItem.custom?.fields.autoDeliveryInterval
    ? props.lineItem.custom.fields.autoDeliveryInterval / 7
    : undefined,
);

const displayPrice = computed(() => {
  const { totalPriceBeforeCartLevelDiscount } = props.lineItem;
  return dollars(totalPriceBeforeCartLevelDiscount) === 0
    ? 'FREE'
    : money(totalPriceBeforeCartLevelDiscount);
});

const imageUrl = computed(() => props.lineItem.titleImage?.small ?? brokenPeanut);

const giftIcon = nutshell['img/gift-box.svg'];
</script>

<template>
  <div class="flex p-4 line-item" data-test="order-summary-line-item">
    <div class="flex w-full text-black">
      <div class="w-12">
        <img :src="imageUrl" :alt="lineItem.name.en" />
      </div>
      <div class="ml-4 w-full">
        <h4
          class="text-sm font-semibold text-black font-sofia-pro"
          data-test="order-summary-product-name"
        >
          {{ lineItem.name.en }}
        </h4>
        <Caption class="my-1 text-neutral-600" data-test="order-summary-product-price">
          <template v-if="lineItem.variant.variantName">
            {{ lineItem.variant.variantName.en }}{{ showPricing ? ' - ' : '' }}
          </template>
          <template v-if="showPricing">{{ money(lineItem.piecePrice) }} each </template>
        </Caption>
        <p
          v-if="lineItem.customizationsDescription"
          class="my-1 text-xs leading-tight font-proxima-nova"
        >
          {{ lineItem.customizationsDescription }}
        </p>
        <div v-if="lineItem.custom?.fields.markedAsGift" class="flex items-center my-4 text-xs">
          <img :src="giftIcon" class="mr-2 w-4" alt="gift" />
          This is a gift
        </div>
        <div class="flex justify-between items-center my-3 price">
          <SmallBodyText class="text-neutral-500"> Qty: {{ lineItem.quantity }} </SmallBodyText>
          <div v-if="showPricing" class="flex">
            <del v-if="lineItem.totalSavings">
              <span class="sr-only">Price reduced from </span>
              <SmallBodyText class="mr-1 text-right text-neutral-500" data-test="strikethrough">
                {{ money(lineItem.totalSavings.comparisonPrice) }}
              </SmallBodyText>
            </del>
            <SmallBodyText
              class="font-semibold text-right"
              data-test="discounted-price"
              :class="[lineItem.totalSavings?.onSale ? 'text-nuts-red-800' : 'text-black']"
            >
              <span v-if="lineItem.totalSavings" class="sr-only">to </span>
              {{ displayPrice }}
            </SmallBodyText>
          </div>
        </div>
        <p
          v-if="isDigitalGift"
          class="mt-4 text-sm underline cursor-pointer pointer-events-auto"
          data-test="digital-gift-see-details-checkout"
          @click.prevent="handleOpenDetails"
        >
          View gift details
        </p>
        <div
          v-if="autoDeliveryIntervalWeeks"
          class="auto-delivery"
          data-test="order-summary-auto-delivery"
        >
          <ADLineItemDetails
            :autoDeliveryIntervalWeeks="autoDeliveryIntervalWeeks"
            :showEditLink="isReceiptPage"
          />
        </div>
      </div>
      <Teleport v-if="isDigitalGift" to="#teleported">
        <GiftDetailsModal
          :isOpen="showGiftDetails"
          :digitalGiftingFields="digitalGiftingFields"
          @close-details="handleCloseDetails"
        />
      </Teleport>
    </div>
  </div>
</template>
